import type { QuizletFilters } from '@/types';
import type { DeckGroupFilters } from '@/types/deck-group';
import type { ArticleFilters, PublicArticleFilters } from '@/types/article';

// Analytics filters type (will be fully defined in types/analytics.ts)
type AnalyticsFilters = Record<string, any>;

// Hierarchical query keys for granular cache management
export const queryKeys = {
  quizlets: {
    all: ['quizlets'] as const,
    lists: () => [...queryKeys.quizlets.all, 'list'] as const,
    list: (filters: QuizletFilters = {}) =>
      [...queryKeys.quizlets.lists(), filters] as const,
    details: () => [...queryKeys.quizlets.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.quizlets.details(), id] as const,
  },
  deckGroups: {
    all: ['deck-groups'] as const,
    lists: () => [...queryKeys.deckGroups.all, 'list'] as const,
    list: (filters: DeckGroupFilters = {}) =>
      [...queryKeys.deckGroups.lists(), filters] as const,
    details: () => [...queryKeys.deckGroups.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.deckGroups.details(), id] as const,
    session: (id: string) =>
      [...queryKeys.deckGroups.all, 'session', id] as const,
  },
  dailyKnowledge: {
    all: ['daily-knowledge'] as const,
    questions: () => [...queryKeys.dailyKnowledge.all, 'questions'] as const,
  },
  sessions: {
    all: ['sessions'] as const,
    detail: (id: string) => [...queryKeys.sessions.all, id] as const,
  },
  generation: {
    all: ['generation'] as const,
    generated: () => [...queryKeys.generation.all, 'generated'] as const,
  },
  subscriptions: {
    all: ['subscriptions'] as const,
    current: () => [...queryKeys.subscriptions.all, 'current'] as const,
  },
  userProfile: {
    all: ['user-profile'] as const,
    current: () => [...queryKeys.userProfile.all, 'current'] as const,
  },
  usage: {
    all: ['usage'] as const,
    current: () => [...queryKeys.usage.all, 'current'] as const,
    daily: () => [...queryKeys.usage.all, 'daily'] as const,
  },
  quizCount: {
    all: ['quiz-count'] as const,
    total: () => [...queryKeys.quizCount.all, 'total'] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    global: (filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'global', filters] as const,
    quizlet: (id: string, filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'quizlet', id, filters] as const,
    dailyKnowledge: (filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'daily-knowledge', filters] as const,
    question: (id: string) =>
      [...queryKeys.analytics.all, 'question', id] as const,
    streak: () =>
      [...queryKeys.analytics.all, 'streak'] as const,
    insights: (filters: AnalyticsFilters) =>
      [...queryKeys.analytics.all, 'insights', filters] as const,
    preferences: () =>
      [...queryKeys.analytics.all, 'preferences'] as const,
  },
  admin: {
    all: ['admin'] as const,
    status: () => [...queryKeys.admin.all, 'status'] as const,
    users: () => [...queryKeys.admin.all, 'users'] as const,
    usersList: (params: any) => [...queryKeys.admin.users(), params] as const,
    userDetails: (userId: string) => [...queryKeys.admin.all, 'user', userId] as const,
  },
  articles: {
    all: ['articles'] as const,
    lists: () => [...queryKeys.articles.all, 'list'] as const,
    list: (filters: ArticleFilters = {}) =>
      [...queryKeys.articles.lists(), filters] as const,
    publicLists: () => [...queryKeys.articles.all, 'public'] as const,
    publicList: (filters: PublicArticleFilters = {}) =>
      [...queryKeys.articles.publicLists(), filters] as const,
    details: () => [...queryKeys.articles.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.articles.details(), id] as const,
    comments: (articleId: string) =>
      [...queryKeys.articles.all, 'comments', articleId] as const,
    shares: (articleId: string) =>
      [...queryKeys.articles.all, 'shares', articleId] as const,
    topics: () => [...queryKeys.articles.all, 'topics'] as const,
    analytics: () => [...queryKeys.articles.all, 'analytics'] as const,
    recordings: (articleId: string) =>
      [...queryKeys.articles.all, 'recordings', articleId] as const,
  },
  articleCollections: {
    all: ['article-collections'] as const,
    lists: () => [...queryKeys.articleCollections.all, 'list'] as const,
    details: () => [...queryKeys.articleCollections.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.articleCollections.details(), id] as const,
    tree: (collectionId: string) =>
      [...queryKeys.articleCollections.all, 'tree', collectionId] as const,
  },
};
